import workflowService from "../services/workflow";

/**
 * Convert local date and time to UTC ISO string
 * @param {Date|string} dateValue - Date object or date string (YYYY-MM-DD)
 * @param {string} timeValue - Time string in 12-hour format (HH:MM AM/PM)
 * @returns {string|null} UTC ISO string or null if invalid
 */
function convertLocalDateTimeToUTC(dateValue, timeValue) {
  if (!dateValue || !timeValue) {
    return null;
  }

  try {
    // Step 1: Convert dateValue to Date object if it's not already
    const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);

    // Step 2: Parse the time (HH:MM AM/PM format) and add to date in milliseconds
    const timeStr = timeValue.trim();
    const timeParts = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);

    if (timeParts) {
      let hours = parseInt(timeParts[1]);
      const minutes = parseInt(timeParts[2]);
      const period = timeParts[3].toUpperCase();

      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      // Add time to date in milliseconds
      const timeInMs = (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
      const finalDate = new Date(dateObj.getTime() + timeInMs);

      // Step 3: Convert to UTC format
      if (!isNaN(finalDate.getTime())) {
        return finalDate.toISOString();
      }
    }

    return null;
  } catch (error) {
    console.error('Error converting local date/time to UTC:', error);
    return null;
  }
}

/**
 * Convert Date object to ISO 8601 string (for program dates)
 * @param {Date} dateObj - Date object
 * @returns {string|null} ISO 8601 string or null if invalid
 */
function convertDateToISO(dateObj) {
  if (!dateObj || !(dateObj instanceof Date)) {
    return null;
  }

  try {
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toISOString();
    }
    return null;
  } catch (error) {
    console.error('Error converting date to ISO:', error);
    return null;
  }
}

/**
 * Convert UTC ISO string to local date and time
 * @param {string} utcString - UTC ISO string
 * @returns {Object} Object with dueDate (Date object) and dueTime (string in HH:MM AM/PM format)
 */
function convertUTCToLocalDateTime(utcString) {
  if (!utcString) {
    return { dueDate: null, dueTime: '' };
  }

  try {
    // Step 1: Convert UTC to local
    const localDate = new Date(utcString);

    // Step 2: Extract year, month, day and create Date object for dueDate
    const year = localDate.getFullYear();
    const month = localDate.getMonth();
    const day = localDate.getDate();
    const dueDate = new Date(year, month, day);

    // Step 3: Extract hour, minute and format dueTime
    let hours = localDate.getHours();
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    if (hours > 12) {
      hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    const dueTime = `${String(hours).padStart(2, '0')}:${minutes} ${period}`;

    return { dueDate, dueTime };
  } catch (error) {
    console.error('Error converting UTC to local date/time:', error);
    return { dueDate: null, dueTime: '' };
  }
}

/**
 * Get all workflows
 * @returns {Promise} API response with workflows array
 */
export async function getAllWorkflowsApi() {
  return workflowService.get('/workflows');
}

/**
 * Get all programs (workflows with is_program=true)
 * @returns {Promise} API response with programs array
 */
export async function getAllProgramsApi() {
  return workflowService.get('/workflows', {
    params: {
      is_program: true
    }
  });
}

/**
 * Convert backend workflow data to frontend canvas format
 * Transforms the workflow definition from API response into VueFlow nodes and edges
 * 
 * @param {Object} backendWorkflow - Workflow object from backend API
 * @param {string} backendWorkflow.id - Workflow ID
 * @param {string} backendWorkflow.name - Workflow name
 * @param {string} backendWorkflow.description - Workflow description
 * @param {Object} backendWorkflow.definition - Workflow definition containing nodes and connections
 * @param {Array} backendWorkflow.definition.nodes - Array of workflow nodes from backend
 * @param {Array} backendWorkflow.definition.connections - Array of connections from backend
 * @param {boolean} backendWorkflow.is_program - Whether this is a program
 * @param {Object} backendWorkflow.duration - Program duration object
 * @param {string} backendWorkflow.duration.type - Duration type ("fixed" or "rolling")
 * @param {string} backendWorkflow.duration.unit - Unit for rolling duration ("days", "weeks", "months")
 * @param {number} backendWorkflow.duration.value - Value for rolling duration
 * @param {string} backendWorkflow.duration.start_date - Start date ISO string for fixed duration
 * @param {string} backendWorkflow.duration.end_date - End date ISO string for fixed duration
 * @param {number} backendWorkflow.price - Program price
 * @returns {Object} Frontend canvas data format
 * @returns {string} return.workflowId - Workflow ID
 * @returns {string} return.workflowTitle - Workflow title
 * @returns {string} return.workflowDescription - Workflow description
 * @returns {Array} return.nodes - VueFlow nodes array
 * @returns {Array} return.edges - VueFlow edges array
 * @returns {Array} return.savedConfigurations - Saved configurations array
 * @returns {Object} return.programSettings - Program settings (if is_program is true)
 */
export function convertBackendToFrontend(backendWorkflow) {
  if (!backendWorkflow || !backendWorkflow.definition) {
    console.error('Invalid backend workflow data:', backendWorkflow);
    return {
      workflowId: null,
      workflowTitle: '',
      workflowDescription: '',
      nodes: [],
      edges: [],
      savedConfigurations: [],
      programSettings: null
    };
  }

  const { id, name, description, definition, is_program, duration, maximum_enrollment, price } = backendWorkflow;
  const { nodes: backendNodes = [], connections = [] } = definition;

  // Convert backend nodes to frontend VueFlow nodes
  // Use backend enum values directly as VueFlow node types
  const frontendNodes = [];
  const savedConfigurations = [];
  const logicNodeMap = new Map(); // Track logic nodes for edge reconstruction

  backendNodes.forEach((backendNode, index) => {
    const { id: nodeId, type, subtype, config = {}, position = { x: 0, y: 0 } } = backendNode;

    // Use config.subtype as fallback if top-level subtype doesn't map correctly
    let actualSubtype = subtype;
    if (!subtype && config.subtype) {
      actualSubtype = config.subtype;
    }

    // Use backend subtype directly as VueFlow node type
    let nodeType = actualSubtype;

    // Build node data from config - always map from backend format
    let nodeData = {
      configured: true, // Mark as configured since it's from backend
      subtype: actualSubtype
    };

    // Store subtype in nodeData
    nodeData.subtype = actualSubtype;

    // Handle specific node type transformations based on subtype
    // Only 6 update triggers have events
    if (type === 'trigger' && (actualSubtype === 'order_update' || actualSubtype === 'appointment_update' ||
      actualSubtype === 'document_update' || actualSubtype === 'report_update' ||
      actualSubtype === 'task_update' || actualSubtype === 'internal_notes_update')) {
      nodeData.events = config.events || [];
    } else if (type === 'trigger' && actualSubtype === 'manual') {
      // Manual trigger only has description
      nodeData.description = config.description || '';
    } else if (type === 'trigger' && actualSubtype === 'scheduled') {
      // Scheduled trigger - ScheduledTriggerConfigV2 format
      nodeData.mode = config.mode || 'time_point';
      if (config.durationConfig) nodeData.durationConfig = config.durationConfig;
      if (config.timePointConfig) nodeData.timePointConfig = config.timePointConfig;
      nodeData.endCondition = config.endCondition || { type: 'never' };
    } else if (type === 'action' && actualSubtype === 'send_message') {
      // Send message action - explicit field mapping to unified format
      // Unified format: {messageType, messageContent, subject(optional), configured}
      nodeData.messageType = config.messageType || '';
      nodeData.messageContent = config.messageContent || '';
      if (config.subject) {
        nodeData.subject = config.subject;
      }
    } else if (type === 'action' && actualSubtype === 'make_appointment') {
      // Appointment action only has requestMessage
      nodeData.requestMessage = config.requestMessage || '';
    } else if (type === 'action' && actualSubtype === 'assign_task') {
      // Assign task action - use title field
      nodeData.title = config.title || '';
      nodeData.description = config.description || '';
      nodeData.assigneeType = config.assigneeType || '';
      nodeData.assigneeName = config.assigneeName || ''; // Preserve name
      nodeData.assigneeId = config.assigneeId || 999997; // Preserve id
      const { dueDate, dueTime } = convertUTCToLocalDateTime(config.dueDate);
      nodeData.dueDate = dueDate;
      nodeData.dueTime = dueTime;
    } else if (type === 'action' && actualSubtype === 'send_document') {
      // Send document action - map backend to frontend
      nodeData.documentType = config.documentType || '';
      nodeData.documents = Array.isArray(config.documents) ? config.documents : [];
      nodeData.deliveryMethod = config.deliveryMethod || '';
    } else if (type === 'action' && actualSubtype === 'place_order') {
      // Place order action - map backend to frontend
      // Check if we have the new payload format or legacy items format
      if (config.payload) {
        nodeData.payload = config.payload;
      } else {
        // Legacy format fallback
        nodeData.orderType = config.orderType || '';
        nodeData.items = Array.isArray(config.items) ? config.items : [];
      }
      // Restore orderItems (VA-format selection list) for preloading on edit
      if (Array.isArray(config.orderItems)) {
        nodeData.orderItems = config.orderItems;
      }
      // Restore DBS version (Home Collection toggle state)
      if (config.isDbsVersion != null) {
        nodeData.isDbsVersion = config.isDbsVersion;
      }
    } else if (type === 'action' && actualSubtype === 'add_internal_notes') {
      // Add internal notes action - map backend to frontend
      nodeData.notes = config.notes || '';
    } else if (type === 'logic') {
      // Logic nodes - store for edge reconstruction
      // Remove logicType from config as we use type and subtype for mapping
      const cleanConfig = { ...config };
      delete cleanConfig.logicType;

      logicNodeMap.set(nodeId, {
        subtype: actualSubtype,  // Use subtype (e.g., 'review_approval', 'wait')
        config: cleanConfig,
        nodeData: nodeData
      });
      // Skip adding logic nodes to frontendNodes - they'll be added to edges
      return;
    }

    // Create VueFlow node using backend enum value as type
    const vueFlowNode = {
      id: nodeId,
      type: nodeType,
      position: position,
      data: nodeData
    };

    frontendNodes.push(vueFlowNode);

    // Add to saved configurations
    savedConfigurations.push({
      id: nodeId,
      type: 'node',
      data: nodeData
    });
  });

  // Convert backend connections to frontend edges
  const frontendEdges = [];
  const processedConnections = new Set();

  connections.forEach((connection, index) => {
    const { id: connId, source_node_id, target_node_id } = connection;

    // Check if this connection involves a logic node
    const sourceLogic = logicNodeMap.get(source_node_id);
    const targetLogic = logicNodeMap.get(target_node_id);

    if (sourceLogic || targetLogic) {
      // This connection involves a logic node - need to reconstruct as edge with logic
      // Find the actual source and target (skipping logic nodes)
      let actualSource = source_node_id;
      let actualTarget = target_node_id;
      let logicData = null;

      // If source is logic, find what connects TO the logic node
      if (sourceLogic) {
        const incomingConn = connections.find(c => c.target_node_id === source_node_id);
        if (incomingConn) {
          actualSource = incomingConn.source_node_id;
          logicData = sourceLogic;
        }
      }

      // If target is logic, find what connects FROM the logic node
      if (targetLogic) {
        const outgoingConn = connections.find(c => c.source_node_id === target_node_id);
        if (outgoingConn) {
          actualTarget = outgoingConn.target_node_id;
          logicData = targetLogic;
        }
      }

      // Create connection key to avoid duplicates
      const connectionKey = `${actualSource}-${actualTarget}`;
      if (processedConnections.has(connectionKey)) {
        return; // Skip duplicate
      }
      processedConnections.add(connectionKey);

      // Create edge with logic data
      const edgeId = `edge-${actualSource}-${actualTarget}`;
      const edge = {
        id: edgeId,
        source: actualSource,
        target: actualTarget,
        type: 'custom',
        data: {}
      };

      // Add logic configuration to edge data - always map from backend format
      if (logicData) {
        const config = logicData.config || {};

        if (logicData.subtype === 'review_approval') {
          // Map backend field names to frontend for review/approval logic
          edge.data = {
            logicCardType: 'review',
            logicCardConfigured: true,
            logicCardData: {
              primaryReviewer: config.primaryReviewer || '',
              hasEscalation: config.hasEscalateReviewer || false,
              escalationReviewer: config.escalateReview || '',
              reviewResponseTime: config.responseTime?.value || 0,
              reviewResponseTimeUnit: config.responseTime?.unit || 'hours',
              actionOnTimeout: config.actionOnTimeout || '',
              hasAIPreScreening: config.useAIReview || false,
              approvalInstructions: config.AIReviewInstruction?.approvalInstruction || '',
              rejectionInstructions: config.AIReviewInstruction?.rejectionInstruction || '',
              escalationInstructions: config.AIReviewInstruction?.escalationInstruction || ''
            }
          };
        } else if (logicData.subtype === 'wait') {
          edge.data = {
            logicCardType: 'wait',
            logicCardConfigured: true,
            logicCardData: {
              mode: config.mode || 'duration',
              ...(config.durationConfig ? { durationConfig: config.durationConfig } : {}),
              ...(config.timePointConfig ? { timePointConfig: config.timePointConfig } : {})
            }
          };
        }

        // Create savedData by copying edge.data and removing nested savedData
        const edgeSavedData = JSON.parse(JSON.stringify(edge.data));
        if (edgeSavedData.savedData) {
          delete edgeSavedData.savedData;
        }

        // Add savedData to edge.data
        edge.data.savedData = edgeSavedData;

        // Add to saved configurations
        savedConfigurations.push({
          id: edgeId,
          type: 'edge',
          data: edge.data
        });
      }
      edge.type = 'logicConnection';

      frontendEdges.push(edge);
    } else {
      // Regular connection without logic
      const connectionKey = `${source_node_id}-${target_node_id}`;
      if (processedConnections.has(connectionKey)) {
        return; // Skip duplicate
      }
      processedConnections.add(connectionKey);

      const edge = {
        id: connId || `edge-${source_node_id}-${target_node_id}`,
        source: source_node_id,
        target: target_node_id,
        type: 'custom',
        data: {}
      };
      edge.type = 'logicConnection';
      frontendEdges.push(edge);
    }
  });
  // Convert program-specific fields if this is a program
  let programSettings = null;
  if (is_program) {
    programSettings = {
      name: name || '',
      description: description || '',
      duration_type: duration?.type || 'rolling',
      start_date: null,
      end_date: null,
      durationValue: '',
      durationUnit: '',
      maximum_enrollment: maximum_enrollment !== undefined ? maximum_enrollment : null,
      price: price || 0
    };

    // Convert duration object to frontend format
    if (duration) {
      if (duration.type === 'fixed') {
        programSettings.start_date = duration.start_date ? new Date(duration.start_date) : null;
        programSettings.end_date = duration.end_date ? new Date(duration.end_date) : null;
      } else if (duration.type === 'rolling') {
        programSettings.durationValue = duration.value || 30;
        programSettings.durationUnit = duration.unit || 'days';
      }
    }
  }

  return {
    workflowId: id,
    workflowTitle: name || '',
    workflowDescription: description || '',
    nodes: frontendNodes,
    edges: frontendEdges,
    savedConfigurations: savedConfigurations,
    programSettings: programSettings
  };
}

/**
 * Convert scheduled trigger data to cron expression
 * @param {string|Date} startDate - Start date (e.g., "2024-01-15" or Date object)
 * @param {string} startTime - Start time (e.g., "9:00 AM", "14:30")
 * @param {string|number} frequencyValue - Frequency value (e.g., "1", "2")
 * @param {string} frequencyUnit - Frequency unit ("Days", "Weeks", "Months")
 * @returns {string} Cron expression (e.g., "0 9 * * *")
 */
export function convertToCron(startDate, startTime, frequencyValue, frequencyUnit) {
  // Parse start time to get hour and minute
  let hour = 9;
  let minute = 0;

  if (startTime) {
    // Handle formats like "9:00 AM", "14:30", "09:00"
    const timeStr = startTime.toString().trim();
    const isPM = timeStr.toLowerCase().includes('pm');
    const isAM = timeStr.toLowerCase().includes('am');

    // Extract time parts
    const timeParts = timeStr.replace(/[ap]m/gi, '').trim().split(':');
    if (timeParts.length >= 2) {
      hour = parseInt(timeParts[0], 10);
      minute = parseInt(timeParts[1], 10);

      // Convert 12-hour to 24-hour format
      if (isPM && hour !== 12) {
        hour += 12;
      } else if (isAM && hour === 12) {
        hour = 0;
      }
    }
  }

  // Parse start date to get day of month and month
  let dayOfMonth = '*';
  let month = '*';
  let dayOfWeek = '*';

  if (startDate) {
    const date = startDate instanceof Date ? startDate : new Date(startDate);
    if (!isNaN(date.getTime())) {
      dayOfMonth = date.getDate();
      month = date.getMonth() + 1; // JavaScript months are 0-indexed
      dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    }
  }

  // Parse frequency
  const freq = parseInt(frequencyValue, 10) || 1;

  // Build cron expression based on frequency unit
  // Cron format: minute hour day-of-month month day-of-week
  let cronExpression;

  switch (frequencyUnit) {
    case 'Days':
      // Run every N days at specified time
      if (freq === 1) {
        // Every day
        cronExpression = `${minute} ${hour} * * *`;
      } else {
        // Every N days - use day of month with step
        cronExpression = `${minute} ${hour} */${freq} * *`;
      }
      break;

    case 'Weeks':
      // Run every N weeks on the same day of week at specified time
      if (freq === 1) {
        // Every week on the same day
        cronExpression = `${minute} ${hour} * * ${dayOfWeek}`;
      } else {
        // Every N weeks - cron doesn't support week intervals directly
        // Use day of week with a note that it runs weekly
        cronExpression = `${minute} ${hour} * * ${dayOfWeek}`;
      }
      break;

    case 'Months':
      // Run every N months on the same day at specified time
      if (freq === 1) {
        // Every month on the same day
        cronExpression = `${minute} ${hour} ${dayOfMonth} * *`;
      } else {
        // Every N months - use month with step
        cronExpression = `${minute} ${hour} ${dayOfMonth} */${freq} *`;
      }
      break;

    default:
      // Default to daily at specified time
      cronExpression = `${minute} ${hour} * * *`;
  }

  return cronExpression;
}

/**
 * Transform WorkflowCanvas data to API format
 * @param {Object} canvasData - Data from WorkflowCanvas.vue
 * @param {string} canvasData.workflowTitle - Workflow title
 * @param {string} canvasData.workflowDescription - Workflow description
 * @param {Array} canvasData.nodes - VueFlow nodes array
 * @param {Array} canvasData.edges - VueFlow edges array
 * @param {Array} canvasData.savedConfigurations - Saved configurations array
 * @param {Object} canvasData.programSettings - Optional program settings (for programs only)
 * @returns {Object} Formatted data for API (CreateWorkflowDto)
 */
export function transformWorkflowData(canvasData) {
  const { workflowTitle, workflowDescription, nodes, edges, savedConfigurations, programSettings } = canvasData;

  // Map backend enum values to type and config metadata
  const nodeTypeMap = {
    // Triggers
    'order_update': { type: 'trigger' },
    'appointment_update': { type: 'trigger' },
    'document_update': { type: 'trigger' },
    'report_update': { type: 'trigger' },
    'task_update': { type: 'trigger' },
    'internal_notes_update': { type: 'trigger' },
    'new_patient_created': { type: 'trigger' },
    'manual': { type: 'trigger' },
    'scheduled': { type: 'trigger' },
    // Actions
    'send_message': { type: 'action' },
    'make_appointment': { type: 'action' },
    'assign_task': { type: 'action' },
    'send_document': { type: 'action' },
    'place_order': { type: 'action' },
    'add_internal_notes': { type: 'action' }
  };

  // Transform nodes to API format
  const transformedNodes = nodes.map(node => {
    // node.type is already the backend enum value (e.g., 'order_update', 'send_message')
    const nodeSubtype = node.type;

    // Get type metadata
    let typeMapping = nodeTypeMap[nodeSubtype] || {
      type: 'action'
    };

    // Use savedData from node.data if available, otherwise use node.data
    const nodeData = (node.data && node.data.savedData) ? node.data.savedData : (node.data || {});

    // Build config object - start with base config
    // Always include configured status so backend knows if node is configured
    let config = {
      configured: nodeData.configured || false
    };

    // Add specific config structures based on node subtype
    // Only 6 update triggers have events
    if (nodeSubtype === 'order_update' || nodeSubtype === 'appointment_update' ||
      nodeSubtype === 'document_update' || nodeSubtype === 'report_update' ||
      nodeSubtype === 'task_update' || nodeSubtype === 'internal_notes_update') {
      config.events = nodeData.events || [];
    } else if (nodeSubtype === 'manual') {
      // Manual trigger only has description
      config.description = nodeData.description || '';
    } else if (nodeSubtype === 'scheduled') {
      // Scheduled trigger - ScheduledTriggerConfigV2 format
      config.mode = nodeData.mode || 'time_point';
      if (nodeData.durationConfig) config.durationConfig = nodeData.durationConfig;
      if (nodeData.timePointConfig) config.timePointConfig = nodeData.timePointConfig;
      config.endCondition = nodeData.endCondition || { type: 'never' };
    } else if (nodeSubtype === 'send_message') {
      // Send message action - explicit field mapping to unified format
      // Unified format: {messageType, messageContent, subject(optional), configured}
      config.messageType = nodeData.messageType || '';
      config.messageContent = nodeData.messageContent || '';
      if (nodeData.subject) {
        config.subject = nodeData.subject;
      }
    } else if (nodeSubtype === 'make_appointment') {
      // Appointment action only has requestMessage
      config.requestMessage = nodeData.requestMessage || '';
    } else if (nodeSubtype === 'assign_task') {
      // Assign task action - rename taskTemplateId to title
      config.title = nodeData.title || '';
      config.assigneeType = nodeData.assigneeType || '';
      config.assigneeId = nodeData.assigneeId || 999997;
      config.assigneeName = nodeData.assigneeName || ''; // Preserve name for backend
      config.description = nodeData.description || '';

      // Convert dueDate and dueTime to UTC using helper function
      const utcDate = convertLocalDateTimeToUTC(nodeData.dueDate, nodeData.dueTime);
      if (utcDate) {
        config.dueDate = utcDate;
      }
    } else if (nodeSubtype === 'send_document') {
      // Send document action - map frontend to backend
      config.documentType = nodeData.documentType || '';
      config.documents = Array.isArray(nodeData.documents) ? nodeData.documents : [];
      config.deliveryMethod = nodeData.deliveryMethod || '';
    } else if (nodeSubtype === 'place_order') {
      // Place order action - map frontend to backend
      // Check if we have the new payload format or legacy items format
      if (nodeData.payload) {
        config.payload = nodeData.payload;
      } else {
        // Legacy format fallback
        config.orderType = nodeData.orderType || '';
        config.items = Array.isArray(nodeData.items) ? nodeData.items : [];
      }
      // Persist orderItems (VA-format selection list) for preloading on edit
      if (Array.isArray(nodeData.orderItems) && nodeData.orderItems.length > 0) {
        config.orderItems = nodeData.orderItems;
      }
      // Persist DBS version (Home Collection toggle state)
      if (nodeData.isDbsVersion != null) {
        config.isDbsVersion = nodeData.isDbsVersion;
      }
    } else if (nodeSubtype === 'add_internal_notes') {
      // Add internal notes action - map frontend to backend
      config.notes = nodeData.notes || '';
    }

    return {
      id: node.id,
      type: typeMapping.type,
      subtype: nodeSubtype,
      config: config,
      position: node.position || { x: 0, y: 0 }
    };
  });

  // Process edges: convert logic blocks on connections into separate logic nodes
  const finalNodes = [...transformedNodes];
  const finalConnections = [];
  const logicBlockMappings = []; // Store mapping for reverse transformation
  let logicNodeCounter = 1;

  edges.forEach(edge => {
    // Use savedData from edge.data if available, otherwise use edge.data
    // This ensures only saved configurations are sent to backend
    const edgeData = (edge.data && edge.data.savedData) ? edge.data.savedData : (edge.data || {});

    // Check if edge has logic block
    if (edgeData?.logicCardConfigured && edgeData?.logicCardData) {
      const logicType = edgeData.logicCardType;

      // Create a unique ID for the logic node
      const logicNodeId = `logic-${logicType}-${logicNodeCounter++}`;

      // Create connection IDs
      const conn1Id = `${edge.id}-to-logic`;
      const conn2Id = `${edge.id}-from-logic`;

      // Build logic node config based on type
      let logicNodeConfig = {};
      let logicSubtype = '';

      if (logicType === 'wait') {
        logicSubtype = 'wait';
        const d = edgeData.logicCardData || {};
        logicNodeConfig = {
          logicType: 'wait-logic',
          mode: d.mode || 'duration',
          ...(d.durationConfig ? { durationConfig: d.durationConfig } : {}),
          ...(d.timePointConfig ? { timePointConfig: d.timePointConfig } : {})
        };
      } else if (logicType === 'review') {
        logicSubtype = 'review_approval';

        // Map frontend field names to backend field names - preserve all reviewer fields
        const reviewData = edgeData.logicCardData || {};
        logicNodeConfig = {
          logicType: 'approval-logic',
          primaryReviewer: reviewData.primaryReviewer || '',
          hasEscalateReviewer: reviewData.hasEscalation || false,
          escalateReview: reviewData.escalationReviewer || '',
          responseTime: {
            value: reviewData.reviewResponseTime || 0,
            unit: reviewData.reviewResponseTimeUnit || 'hours'
          },
          actionOnTimeout: reviewData.actionOnTimeout || '',
          useAIReview: reviewData.hasAIPreScreening || false,
          AIReviewInstruction: {
            approvalInstruction: reviewData.approvalInstructions || '',
            rejectionInstruction: reviewData.rejectionInstructions || '',
            escalationInstruction: reviewData.escalationInstructions || ''
          }
        };
      }

      // Create the logic node
      const logicNode = {
        id: logicNodeId,
        type: 'logic',
        subtype: logicSubtype,
        config: logicNodeConfig,
        position: {
          x: 0,
          y: 0
        }
      };

      // Add logic node to final nodes
      finalNodes.push(logicNode);

      // Create first connection: source -> logic node
      const connection1 = {
        id: conn1Id,
        source_node_id: edge.source,
        target_node_id: logicNodeId
      };
      if (edge.sourceHandle) {
        connection1.source_port = edge.sourceHandle;
      }

      // Create second connection: logic node -> target
      const connection2 = {
        id: conn2Id,
        source_node_id: logicNodeId,
        target_node_id: edge.target
      };
      if (edge.targetHandle) {
        connection2.target_port = edge.targetHandle;
      }

      finalConnections.push(connection1, connection2);

      // Store mapping for reverse transformation
      logicBlockMappings.push({
        originalEdgeId: edge.id,
        logicNodeId: logicNodeId,
        connection1Id: conn1Id,
        connection2Id: conn2Id,
        logicType: logicType,
        sourceNodeId: edge.source,
        targetNodeId: edge.target
      });

    } else {
      // No logic block, create normal connection
      const connection = {
        id: edge.id,
        source_node_id: edge.source,
        target_node_id: edge.target
      };

      // Add optional ports if they exist
      if (edge.sourceHandle) {
        connection.source_port = edge.sourceHandle;
      }
      if (edge.targetHandle) {
        connection.target_port = edge.targetHandle;
      }

      finalConnections.push(connection);
    }
  });

  // Build the final API payload matching CreateWorkflowDto interface
  const payload = {
    name: workflowTitle || 'Untitled Workflow',
    description: workflowDescription || '',
    definition: {
      nodes: finalNodes,
      connections: finalConnections,
      metadata: {
        // Store logic block mappings for reverse transformation
        logicBlockMappings: logicBlockMappings,
        version: '1.0',
        created_by: 'workflow-canvas'
      }
    }
  };

  // Add program-specific fields if programSettings is provided
  if (programSettings) {
    // Duration object structure
    if (programSettings.duration_type === 'fixed') {
      payload.duration = {
        type: 'fixed',
        start_date: programSettings.start_date ? convertDateToISO(programSettings.start_date) : null,
        end_date: programSettings.end_date ? convertDateToISO(programSettings.end_date) : null
      };
    } else if (programSettings.duration_type === 'rolling') {
      payload.duration = {
        type: 'rolling',
        unit: programSettings.durationUnit || 'days',
        value: programSettings.durationValue || 30
      };
    }

    // Add maximum_enrollment - convert frontend format to backend format
    // Frontend: null = OFF, number = ON with max value
    // Backend: null = OFF, number = max enrollment limit
    if (programSettings.maximum_enrollment !== undefined) {
      payload.maximum_enrollment = programSettings.maximum_enrollment;
    }

    // Add price if available - ensure it's a number
    if (programSettings.price !== null && programSettings.price !== undefined) {
      payload.price = parseFloat(programSettings.price);
    }
  }

  return payload;
}

// Create a new workflow
export function createWorkflowApi(workflowData) {
  return workflowService({
    url: "/workflows",
    method: "post",
    data: workflowData
  });
}

// Get workflow by ID
export function getWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}`,
    method: "get"
  });
}

// Update workflow
export function updateWorkflowApi(workflowId, workflowData) {
  return workflowService({
    url: `/workflows/${workflowId}`,
    method: "put",
    data: workflowData
  });
}

// Delete workflow
export function deleteWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}`,
    method: "delete"
  });
}

// Get all workflows
export function getWorkflowsApi(params) {
  return workflowService({
    url: "/workflows",
    method: "get",
    params
  });
}

// Publish workflow
export function publishWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/publish`,
    method: "post"
  });
}

// Unpublish workflow
export function unpublishWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/unpublish`,
    method: "post"
  });
}

// Test workflow execution
export function testWorkflowApi(workflowId, testData) {
  return workflowService({
    url: `/workflows/${workflowId}/test`,
    method: "post",
    data: testData
  });
}

// Copy workflow
export function copyWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/copy`,
    method: "post"
  });
}

/**
 * Activate a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response
 * Response: { id, name, status: "active", version, activatedAt, activatedBy }
 */
export function activateWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/activate`,
    method: "put"
  });
}

/**
 * Deactivate a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response
 * Response: { id, name, status: "draft", version, activatedAt, activatedBy }
 */
export function deactivateWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/deactivate`,
    method: "put"
  });
}

/**
 * Archive a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response
 * Response: { workflowId, status: "archived", archivedAt, versionNumber, message }
 */
export function archiveWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/archive`,
    method: "put",
    data: {
      reason: "No longer needed",
      workflowId: workflowId
    }
  });
}

/**
 * Validate a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response
 * Response: { valid: boolean, errors: [], warnings: [] }
 */
export function validateWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/validate`,
    method: "post"
  });
}

/**
 * Get workflow assignees
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response with assignments
 */
export function getWorkflowAssigneesApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/assignments`,
    method: "get"
  });
}

/**
 * Save workflow assignees (patients assigned to workflow)
 * @param {string} workflowId - Workflow ID
 * @param {Object} requestBody - Request body containing assignments and apply_to_new_patients
 * Example: { assignments: [{ patient_id: 1001, status: "active" }], apply_to_new_patients: true }
 * @returns {Promise} API response
 * Response: { success: boolean, workflow_id, summary: { added, updated, removed }, assignments, apply_to_new_patients }
 */
export function saveWorkflowAssigneesApi(workflowId, requestBody) {
  return workflowService({
    url: `/workflows/${workflowId}/assign`,
    method: "post",
    data: requestBody
  });
}

/**
 * Get all workflow instances (all runs, both test and real)
 * @param {string} workflowId - Workflow ID
 * @param {Object} params - Query parameters
 * @returns {Promise} API response with workflow instances
 */
export function getAllWorkflowInstancesApi(workflowId, params = {}) {
  return workflowService({
    url: `/workflows/${workflowId}/instances`,
    method: "get",
    params
  });
}

/**
 * Get workflow instance details by ID
 * @param {string} instanceId - Instance ID
 * @returns {Promise} API response with instance details
 */
export function getWorkflowInstanceByIdApi(instanceId) {
  return workflowService({
    url: `/workflows/instances/${instanceId}`,
    method: "get"
  });
}

/**
 * Check patient enrollment status across all workflows/programs
 * @param {string|number} patientId - Patient ID
 * @returns {Promise} API response with enrollment data
 * Response: { patient_id, enrollments: { "workflow-uuid-1": { status: "active" }, ... } }
 */
export function getEnrollmentCheckApi(patientId) {
  return workflowService({
    url: "/workflows/enrollment-check",
    method: "get",
    params: { patient_id: patientId }
  });
}

/**
 * Generate workflow with AI
 * @param {string} description - User's natural language description of the workflow
 * @param {Object} existingWorkflow - Current workflow data on canvas (optional)
 * @param {boolean} isProgram - Whether the workflow is a program (optional)
 * @returns {Promise} API response with generated workflow
 * Example payload: { description: "help me generate a workflow like this: when order created, send a message in-app, name the workflow as haha", type: "patient", is_program: true, existingWorkflow: {...} }
 */
export function generateWorkflowWithAIApi(description, existingWorkflow = null, isProgram = false) {
  const payload = {
    description: description,
    type: "patient"
  };

  // Add is_program flag if this is a program
  if (isProgram) {
    payload.is_program = true;
  }

  // Add existingWorkflow if provided
  if (existingWorkflow) {
    payload.existingWorkflow = existingWorkflow;
  }

  return workflowService({
    url: "/ai/generate-workflow",
    method: "post",
    data: payload
  });
}

/**
 * Upgrade workflow to program
 * @param {string} workflowId - Workflow ID
 * @param {Object} programData - Program configuration
 * @param {Object} programData.duration - Duration object
 * @param {string} programData.duration.type - "fixed" or "rolling"
 * @param {string} programData.duration.unit - Unit for rolling ("days", "weeks", "months")
 * @param {number} programData.duration.value - Value for rolling duration
 * @param {string} programData.duration.start_date - Start date ISO string for fixed
 * @param {string} programData.duration.end_date - End date ISO string for fixed
 * @param {number} programData.price - Optional program price
 * @returns {Promise} API response with updated workflow
 */
export function upgradeWorkflowToProgramApi(workflowId, programData) {
  return workflowService({
    url: `/workflows/${workflowId}/upgrade-to-program`,
    method: "post",
    data: programData
  });
}

/**
 * Downgrade program to workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise} API response with updated workflow
 */
export function downgradeProgramToWorkflowApi(workflowId) {
  return workflowService({
    url: `/workflows/${workflowId}/downgrade-to-workflow`,
    method: "post"
  });
}

/**
 * Check patient enrollment in workflows
 * @param {string|number} patientId - Patient ID
 * @returns {Promise} API response with enrollment data
 * Response: { patient_id, enrollments: { "workflow-uuid-1": { status: "active" }, ... } }
 */
export function checkPatientEnrollmentApi(patientId) {
  return workflowService({
    url: `/workflows/enrollment-check`,
    method: "get",
    params: { patient_id: patientId }
  });
}

/**
 * Batch assign multiple patients to a workflow
 * Can be used to assign one or more patients
 * @param {string} workflowId - Workflow ID
 * @param {Object} data - Request data
 * @param {Array<number>} data.patient_ids - Array of patient IDs to assign (can be single patient)
 * @returns {Promise} API response with batch assignment results
 * Response: { workflow_id, results: [{ patient_id, success, status }], assigned, failed }
 * Errors: 400 (cannot assign to template, enrollment full, program ended), 404 (workflow not found)
 */
export function batchAssignPatientsToWorkflowApi(workflowId, data) {
  return workflowService({
    url: `/workflows/${workflowId}/assignments`,
    method: "post",
    data
  });
}

/**
 * Unassign (remove) a patient from a workflow
 * @param {string} workflowId - Workflow ID
 * @param {string|number} patientId - Patient ID
 * @returns {Promise} API response with removal confirmation
 * Response: { workflow_id, patient_id, status: "removed" }
 * Errors: 404 (assignment not found or already removed), 409 (patient has active instances)
 */
export function unassignPatientFromWorkflowApi(workflowId, patientId) {
  return workflowService({
    url: `/workflows/${workflowId}/assignments/${patientId}`,
    method: "delete"
  });
}

/**
 * Pause a patient's assignment (cascades to all active instances)
 * @param {string} workflowId - Workflow ID
 * @param {string|number} patientId - Patient ID
 * @param {Object} data - Request data
 * @param {string} data.reason - Optional reason for pausing (max 500 chars)
 * @returns {Promise} API response with pause confirmation
 * Response: { workflow_id, patient_id, assignment_status: "paused", affected_instances, reason? }
 * Errors: 404 (assignment not found), 409 (assignment not in active status)
 */
export function pausePatientAssignmentApi(workflowId, patientId, data = {}) {
  return workflowService({
    url: `/workflows/${workflowId}/assignments/${patientId}/pause`,
    method: "put",
    data
  });
}

/**
 * Resume a paused patient assignment (cascades to all paused instances)
 * @param {string} workflowId - Workflow ID
 * @param {string|number} patientId - Patient ID
 * @param {Object} data - Request data
 * @param {string} data.reason - Optional reason for resuming (max 500 chars)
 * @returns {Promise} API response with resume confirmation
 * Response: { workflow_id, patient_id, assignment_status: "active", affected_instances, reason? }
 * Errors: 404 (assignment not found), 409 (assignment not in paused status)
 */
export function resumePatientAssignmentApi(workflowId, patientId, data = {}) {
  return workflowService({
    url: `/workflows/${workflowId}/assignments/${patientId}/resume`,
    method: "put",
    data
  });
}
